// src/utils/mockDataContracts.ts
// Step 6 — Contract Management Module
// Supplier contracts, insurance certificates, compliance documents, pricing agreements

// ── Types ─────────────────────────────────────────────────────────

export type ContractStatus = 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED' | 'PENDING' | 'TERMINATED'

export type DocumentType =
  | 'PUBLIC_LIABILITY'
  | 'PROFESSIONAL_INDEMNITY'
  | 'WORKERS_COMP'
  | 'MOTOR_VEHICLE'
  | 'TOOL_THEFT'
  | 'SMSF_CERT'
  | 'WHS_POLICY'
  | 'SWMS_MASTER'
  | 'ELECTRICAL_LICENCE'
  | 'CONTRACTOR_LICENCE'
  | 'TMR_ACCREDITATION'
  | 'ISO_CERT'
  | 'PRICING_SCHEDULE'

export type ContractType =
  | 'SUPPLY_AGREEMENT'
  | 'SUBCONTRACT'
  | 'PREFERRED_SUPPLIER'
  | 'MASTER_SERVICES'
  | 'SPOT_RATE'

// ── Display metadata ───────────────────────────────────────────────

export const CONTRACT_STATUS_META: Record<ContractStatus, { label: string; bg: string; text: string; dot: string }> = {
  ACTIVE:         { label: 'Active',          bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  EXPIRING_SOON:  { label: 'Expiring <90d',   bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  EXPIRED:        { label: 'Expired',         bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  PENDING:        { label: 'Pending review',  bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  TERMINATED:     { label: 'Terminated',      bg: 'bg-gray-100',   text: 'text-gray-400',   dot: 'bg-gray-300'   },
}

export const CONTRACT_TYPE_META: Record<ContractType, { label: string; icon: string }> = {
  SUPPLY_AGREEMENT:  { label: 'Supply agreement',    icon: '📦' },
  SUBCONTRACT:       { label: 'Subcontract',         icon: '🔧' },
  PREFERRED_SUPPLIER:{ label: 'Preferred supplier',  icon: '⭐' },
  MASTER_SERVICES:   { label: 'Master services',     icon: '📋' },
  SPOT_RATE:         { label: 'Spot rate only',      icon: '🏷'  },
}

export const DOC_TYPE_META: Record<DocumentType, { label: string; short: string }> = {
  PUBLIC_LIABILITY:      { label: 'Public liability insurance',       short: 'Public liability'   },
  PROFESSIONAL_INDEMNITY:{ label: 'Professional indemnity insurance', short: 'Prof. indemnity'    },
  WORKERS_COMP:          { label: 'WorkCover / workers compensation', short: 'WorkCover'          },
  MOTOR_VEHICLE:         { label: 'Motor vehicle / fleet insurance',  short: 'Motor vehicle'      },
  TOOL_THEFT:            { label: 'Tools & equipment insurance',      short: 'Tool & equipment'   },
  SMSF_CERT:             { label: 'Subcontractor management cert',    short: 'SMSF cert'          },
  WHS_POLICY:            { label: 'WHS management policy',           short: 'WHS policy'         },
  SWMS_MASTER:           { label: 'Master SWMS register',            short: 'Master SWMS'        },
  ELECTRICAL_LICENCE:    { label: 'Electrical contractor licence',   short: 'Electrical lic.'    },
  CONTRACTOR_LICENCE:    { label: 'QBCC contractor licence',         short: 'QBCC licence'       },
  TMR_ACCREDITATION:     { label: 'TMR pre-qualification',           short: 'TMR pre-qual'       },
  ISO_CERT:              { label: 'ISO 9001 / 45001 certification',  short: 'ISO cert'           },
  PRICING_SCHEDULE:      { label: 'Agreed pricing schedule',         short: 'Pricing schedule'   },
}

// ── Interfaces ─────────────────────────────────────────────────────

export interface ComplianceDocument {
  id: string
  type: DocumentType
  title: string
  issuer: string
  policyOrRefNumber?: string
  coverAmount?: number         // $ (for insurance)
  issueDate: string
  expiryDate: string
  filePath?: string
  isUploaded: boolean
  alertDaysBefore: number      // warn this many days before expiry
}

export interface PricingTerm {
  id: string
  itemDescription: string
  catalogueSku?: string
  agreedRate: number
  unit: string
  effectiveFrom: string
  effectiveTo?: string
  escalationClause?: string    // e.g. 'CPI + 2% annual'
  notes?: string
}

export interface SupplierContract {
  id: string
  contractNumber: string       // MCB-CONT-2024-001
  supplierId: string
  supplierName: string
  supplierABN: string
  supplierContact: string
  supplierEmail: string
  contractType: ContractType
  status: ContractStatus
  title: string
  description: string
  startDate: string
  endDate?: string
  renewalNoticeDate?: string   // date by which renewal must be triggered
  totalValue?: number          // for fixed-value contracts
  annualValue?: number         // for annual spend agreements
  currency: 'AUD'
  paymentTermsDays: number
  documents: ComplianceDocument[]
  pricingTerms: PricingTerm[]
  notes?: string
  createdById: string
  createdByName: string
  createdAt: string
  updatedAt: string
  lastReviewedAt?: string
  reviewedByName?: string
}

// ── Helper: days until expiry ──────────────────────────────────────

export function daysUntilExpiry(expiryDate: string): number {
  return Math.ceil((new Date(expiryDate).getTime() - Date.now()) / 86_400_000)
}

export function docStatus(doc: ComplianceDocument): 'VALID' | 'EXPIRING' | 'EXPIRED' {
  const days = daysUntilExpiry(doc.expiryDate)
  if (days < 0)                        return 'EXPIRED'
  if (days <= doc.alertDaysBefore)     return 'EXPIRING'
  return 'VALID'
}

export function contractStatus(c: SupplierContract): ContractStatus {
  if (c.status === 'TERMINATED') return 'TERMINATED'
  if (!c.endDate) return c.status
  const days = daysUntilExpiry(c.endDate)
  if (days < 0)   return 'EXPIRED'
  if (days <= 90) return 'EXPIRING_SOON'
  return 'ACTIVE'
}

export function getExpiringDocuments(contracts: SupplierContract[], withinDays = 90): {
  contract: SupplierContract; doc: ComplianceDocument; days: number
}[] {
  const results: { contract: SupplierContract; doc: ComplianceDocument; days: number }[] = []
  for (const c of contracts) {
    for (const doc of c.documents) {
      const days = daysUntilExpiry(doc.expiryDate)
      if (days <= withinDays) results.push({ contract: c, doc, days })
    }
  }
  return results.sort((a, b) => a.days - b.days)
}

// ── Mock contracts ─────────────────────────────────────────────────

export const MOCK_CONTRACTS: SupplierContract[] = [

  // 1 — Aldridge Traffic Architects — Preferred supplier
  {
    id: 'cont-001',
    contractNumber: 'MCB-CONT-2024-001',
    supplierId: 'sup-aldridge',
    supplierName: 'Aldridge Traffic Architects Pty Ltd',
    supplierABN: '42 007 135 844',
    supplierContact: 'Marcus Reid',
    supplierEmail: 'mreid@aldridgetraffic.com.au',
    contractType: 'PREFERRED_SUPPLIER',
    status: 'ACTIVE',
    title: 'Preferred supplier agreement — traffic signal equipment',
    description: 'Annual preferred supplier agreement for supply of TMR-approved traffic signal equipment including LED lanterns, SCATS controllers, push buttons, and associated components. Fixed pricing schedule with annual CPI escalation.',
    startDate: '2024-07-01',
    endDate: '2026-12-31',
    renewalNoticeDate: '2026-10-01',
    annualValue: 280_000,
    currency: 'AUD',
    paymentTermsDays: 30,
    documents: [
      { id: 'doc-c001-1', type: 'PUBLIC_LIABILITY',       title: 'Public liability $20M',           issuer: 'QBE Insurance',      policyOrRefNumber: 'QBE-PL-2024-8841', coverAmount: 20_000_000, issueDate: '2024-07-01', expiryDate: '2026-07-01', isUploaded: true,  alertDaysBefore: 90 },
      { id: 'doc-c001-2', type: 'WORKERS_COMP',           title: 'WorkCover QLD',                   issuer: 'WorkCover QLD',      policyOrRefNumber: 'WC-QLD-441892',                           issueDate: '2025-07-01', expiryDate: '2026-07-01', isUploaded: true,  alertDaysBefore: 60 },
      { id: 'doc-c001-3', type: 'PROFESSIONAL_INDEMNITY', title: 'Professional indemnity $2M',      issuer: 'Chubb Australia',    policyOrRefNumber: 'CHB-PI-441-2024',  coverAmount: 2_000_000,  issueDate: '2024-07-01', expiryDate: '2026-07-01', isUploaded: true,  alertDaysBefore: 90 },
      { id: 'doc-c001-4', type: 'PRICING_SCHEDULE',       title: 'Pricing schedule 2024–2026',      issuer: 'Aldridge',           policyOrRefNumber: 'ATA-PSC-2024-MCB',                        issueDate: '2024-07-01', expiryDate: '2026-12-31', isUploaded: true,  alertDaysBefore: 90 },
    ],
    pricingTerms: [
      { id: 'pt-c001-1', itemDescription: 'LED signal head 3-aspect 300mm (TMR TAER)',   catalogueSku: 'cat-006', agreedRate: 680,    unit: 'each', effectiveFrom: '2024-07-01', effectiveTo: '2026-12-31', escalationClause: 'CPI + 1% from 1 Jul 2026' },
      { id: 'pt-c001-2', itemDescription: 'R900S SCATS controller cabinet',              catalogueSku: 'cat-007', agreedRate: 14_800, unit: 'each', effectiveFrom: '2024-07-01', effectiveTo: '2026-12-31', escalationClause: 'CPI + 1% from 1 Jul 2026' },
      { id: 'pt-c001-3', itemDescription: 'LED pedestrian lantern — green/red',                                   agreedRate: 420,    unit: 'each', effectiveFrom: '2024-07-01', effectiveTo: '2026-12-31' },
    ],
    notes: 'MCB CE is on the Aldridge preferred contractor list for ITS and signals projects in SEQ. 14-day lead time on standard stock; 6–8 weeks for non-standard equipment.',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2024-06-28T10:00:00Z', updatedAt: '2026-01-10T09:00:00Z',
    lastReviewedAt: '2026-01-10T09:00:00Z', reviewedByName: 'Jane Smith',
  },

  // 2 — Prysmian/Olex — Supply agreement
  {
    id: 'cont-002',
    contractNumber: 'MCB-CONT-2024-003',
    supplierId: 'sup-olex',
    supplierName: 'Prysmian Group Australia (Olex)',
    supplierABN: '67 091 231 772',
    supplierContact: 'Sharon Bartlett',
    supplierEmail: 's.bartlett@prysmian.com',
    contractType: 'SUPPLY_AGREEMENT',
    status: 'ACTIVE',
    title: 'Cable supply agreement — power cables',
    description: 'Annual supply agreement for Olex power cables. Base rates are fixed for 6 months, with a copper surcharge mechanism applied monthly based on LME copper spot price. MCB CE must obtain a written price update before placing any order exceeding $10,000.',
    startDate: '2025-01-01',
    endDate: '2026-12-31',
    renewalNoticeDate: '2026-10-01',
    annualValue: 95_000,
    currency: 'AUD',
    paymentTermsDays: 30,
    documents: [
      { id: 'doc-c002-1', type: 'PUBLIC_LIABILITY', title: 'Public liability $20M', issuer: 'CGU Insurance', policyOrRefNumber: 'CGU-PL-2025-19241', coverAmount: 20_000_000, issueDate: '2025-01-01', expiryDate: '2026-01-01', isUploaded: true, alertDaysBefore: 90 },
      { id: 'doc-c002-2', type: 'PRICING_SCHEDULE', title: 'Cable pricing schedule + copper surcharge formula', issuer: 'Prysmian Group', policyOrRefNumber: 'PRY-MCB-PSC-2025', issueDate: '2025-01-01', expiryDate: '2026-12-31', isUploaded: true, alertDaysBefore: 60 },
    ],
    pricingTerms: [
      { id: 'pt-c002-1', itemDescription: '3C+E 6mm² Cu/XLPE/PVC cable',   catalogueSku: 'cat-001', agreedRate: 14.90, unit: 'm', effectiveFrom: '2025-01-01', notes: 'Base rate Jan 2025. Copper surcharge +$1.90/m applied Jun 2026 (LME Cu elevated). Check portal before ordering.' },
      { id: 'pt-c002-2', itemDescription: '3C+E 16mm² Cu/XLPE/PVC cable',  catalogueSku: 'cat-002', agreedRate: 34.20, unit: 'm', effectiveFrom: '2025-01-01', notes: 'Base rate Jan 2025. Copper surcharge applied proportionally. Current rate $38.50/m.' },
    ],
    notes: '⚠ COPPER SURCHARGE: LME copper prices are elevated. Price surcharge mechanism active as of Jun 2026. Confirm current rate with Sharon Bartlett before ordering cable >$5,000. Catalogue will update automatically from invoice import.',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2024-12-20T10:00:00Z', updatedAt: '2026-06-16T09:00:00Z',
    lastReviewedAt: '2026-06-16T09:00:00Z', reviewedByName: 'Jane Smith',
  },

  // 3 — Saferoads — Preferred supplier
  {
    id: 'cont-003',
    contractNumber: 'MCB-CONT-2025-001',
    supplierId: 'sup-saferoads',
    supplierName: 'Saferoads Queensland Pty Ltd',
    supplierABN: '89 124 603 271',
    supplierContact: 'Brett Anning',
    supplierEmail: 'b.anning@saferoads.com.au',
    contractType: 'PREFERRED_SUPPLIER',
    status: 'ACTIVE',
    title: 'Preferred supplier — civil materials and roadside equipment',
    description: 'Preferred supplier agreement for civil materials including UPVC conduit, cable pits, DIN base plates, and reinforcing steel. Agreed pricing with annual review. Saferoads provides 3-day delivery to BNE and SC depot.',
    startDate: '2025-07-01',
    endDate: '2027-06-30',
    renewalNoticeDate: '2027-04-01',
    annualValue: 120_000,
    currency: 'AUD',
    paymentTermsDays: 21,
    documents: [
      { id: 'doc-c003-1', type: 'PUBLIC_LIABILITY', title: 'Public liability $20M', issuer: 'Allianz', policyOrRefNumber: 'ALZ-2025-88441', coverAmount: 20_000_000, issueDate: '2025-07-01', expiryDate: '2027-07-01', isUploaded: true, alertDaysBefore: 90 },
      { id: 'doc-c003-2', type: 'WORKERS_COMP',     title: 'WorkCover QLD', issuer: 'WorkCover QLD', policyOrRefNumber: 'WC-QLD-882914', issueDate: '2025-07-01', expiryDate: '2026-07-01', isUploaded: true, alertDaysBefore: 60 },
      { id: 'doc-c003-3', type: 'PRICING_SCHEDULE', title: 'Civil materials pricing schedule 2025–2027', issuer: 'Saferoads QLD', policyOrRefNumber: 'SRD-PSC-2025-MCB', issueDate: '2025-07-01', expiryDate: '2027-06-30', isUploaded: true, alertDaysBefore: 90 },
    ],
    pricingTerms: [
      { id: 'pt-c003-1', itemDescription: 'UPVC 100mm conduit',    catalogueSku: 'cat-004', agreedRate: 6.40,   unit: 'm',    effectiveFrom: '2025-07-01', escalationClause: 'Annual CPI review 1 Jul 2026'   },
      { id: 'pt-c003-2', itemDescription: 'Cable pit 300×450',      catalogueSku: 'cat-005', agreedRate: 245.00, unit: 'each', effectiveFrom: '2025-07-01', escalationClause: 'Annual CPI review 1 Jul 2026'   },
      { id: 'pt-c003-3', itemDescription: 'DIN base plate (F-2)',   catalogueSku: 'cat-011', agreedRate: 420.00, unit: 'each', effectiveFrom: '2025-07-01', escalationClause: 'Steel-linked — reviewed quarterly' },
      { id: 'pt-c003-4', itemDescription: 'Y16 reinforcing steel',  catalogueSku: 'cat-010', agreedRate: 1_280,  unit: 'tonne',effectiveFrom: '2026-06-01', notes: 'Steel price revised down 11% Jun 2026 (market correction). Current contracted rate $1,280/t.' },
    ],
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2025-06-25T10:00:00Z', updatedAt: '2026-06-04T09:00:00Z',
    lastReviewedAt: '2026-06-04T09:00:00Z', reviewedByName: 'Jane Smith',
  },

  // 4 — Downer EDI — Subcontract
  {
    id: 'cont-004',
    contractNumber: 'MCB-CONT-2026-001',
    supplierId: 'sup-downer',
    supplierName: 'Downer EDI Works Pty Ltd',
    supplierABN: '75 096 278 992',
    supplierContact: 'Rachael Stokes',
    supplierEmail: 'r.stokes@downer.com.au',
    contractType: 'SUBCONTRACT',
    status: 'ACTIVE',
    title: 'Subcontract — asphalt pavement works TP-2026-003',
    description: 'Subcontract for supply and lay of asphalt pavement for Maroochydore Rd Intersection Reconstruction. Fixed price, subject to final quantities per approved drawings.',
    startDate: '2026-07-01',
    endDate: '2026-12-31',
    totalValue: 162_800,
    currency: 'AUD',
    paymentTermsDays: 45,
    documents: [
      { id: 'doc-c004-1', type: 'PUBLIC_LIABILITY',  title: 'Public liability $20M',       issuer: 'Zurich Insurance', policyOrRefNumber: 'ZUR-PL-2026-44128', coverAmount: 20_000_000, issueDate: '2026-01-01', expiryDate: '2027-01-01', isUploaded: true,  alertDaysBefore: 90 },
      { id: 'doc-c004-2', type: 'WORKERS_COMP',      title: 'WorkCover QLD',               issuer: 'WorkCover QLD',    policyOrRefNumber: 'WC-QLD-DOWN-2026',                          issueDate: '2026-01-01', expiryDate: '2027-01-01', isUploaded: true,  alertDaysBefore: 60 },
      { id: 'doc-c004-3', type: 'CONTRACTOR_LICENCE',title: 'QBCC contractor licence (SC)', issuer: 'QBCC',            policyOrRefNumber: 'QBCC-15012944',                             issueDate: '2022-04-01', expiryDate: '2026-09-15', isUploaded: true,  alertDaysBefore: 90 },
      { id: 'doc-c004-4', type: 'WHS_POLICY',        title: 'WHS management policy 2026',  issuer: 'Downer',           policyOrRefNumber: 'DOWN-WHS-2026',                             issueDate: '2026-01-15', expiryDate: '2027-01-15', isUploaded: true,  alertDaysBefore: 60 },
      { id: 'doc-c004-5', type: 'SWMS_MASTER',       title: 'Master SWMS — asphalt works', issuer: 'Downer',           policyOrRefNumber: 'DOWN-SWMS-ASP-001',                         issueDate: '2026-06-01', expiryDate: '2027-06-01', isUploaded: false, alertDaysBefore: 60 },
    ],
    pricingTerms: [
      { id: 'pt-c004-1', itemDescription: 'Asphalt pavement supply and lay — lump sum', agreedRate: 162_800, unit: 'item', effectiveFrom: '2026-07-01', effectiveTo: '2026-12-31', notes: 'Lump sum subject to final area verification per approved drawings. Variation mechanism per AS 4000.' },
    ],
    notes: '⚠ Downer QBCC licence expires 15 Sep 2026 — renew before work commences or request updated certificate from Rachael Stokes. SWMS for asphalt works not yet uploaded — required before site access.',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-05-20T10:00:00Z', updatedAt: '2026-06-10T09:00:00Z',
  },

  // 5 — Blackwoods — no formal contract (spot rate)
  {
    id: 'cont-005',
    contractNumber: 'MCB-CONT-2025-SPOT-001',
    supplierId: 'sup-blackwoods',
    supplierName: 'Blackwoods Australia',
    supplierABN: '59 004 400 405',
    supplierContact: 'Account services',
    supplierEmail: 'business@blackwoods.com.au',
    contractType: 'SPOT_RATE',
    status: 'PENDING',
    title: 'Trade account — PPE and consumables (no formal contract)',
    description: 'Blackwoods trade account for PPE, consumables, and tools. No formal supply agreement in place — purchases are at catalogue price. PM has flagged the need to negotiate a preferred pricing agreement to reduce PPE costs.',
    startDate: '2020-01-01',
    currency: 'AUD',
    paymentTermsDays: 30,
    documents: [
      { id: 'doc-c005-1', type: 'PUBLIC_LIABILITY', title: 'Public liability $20M', issuer: 'IAG Insurance', policyOrRefNumber: 'IAG-PL-2026-91028', coverAmount: 20_000_000, issueDate: '2026-01-01', expiryDate: '2027-01-01', isUploaded: false, alertDaysBefore: 90 },
    ],
    pricingTerms: [],
    notes: 'ACTION REQUIRED: Negotiate a formal preferred pricing agreement with Blackwoods. Current spend is ~$12,840 YTD at catalogue prices. A preferred agreement could save 8–12%. Contact account manager to initiate.',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2025-08-01T10:00:00Z', updatedAt: '2026-06-20T09:00:00Z',
  },
]
